import React, { useContext, useRef, useEffect, useLayoutEffect, useState } from "react";
import { useTheme } from 'styled-components'
import { QueueContext } from "../../context/QueueProvider";
import { Container } from "./styles";
import { useGemscape } from "./hooks/useGemscape";
import { computeLiveFractions } from "./gemscape-cache";
import { useShapeActivation } from "./hooks/useShapeActivation";
import { usePlaybackProgress } from "./hooks/usePlaybackProgress";
import { usePulseLoop } from "./hooks/usePulseLoop";

/**
 * Snap a seek percentage to the nearest bar-line using track tempo metadata.
 * Returns the original value unchanged when any required field is missing.
 */
function snapToBar(seekPercent, trackObj) {
  try {
    const { duration, bpm, signature, initialBeat } = trackObj || {};
    if (!duration || !bpm || bpm < 60 || !signature) return seekPercent;
    const beatDur = 60 / bpm;
    const barDur = beatDur * signature;
    const offset = (initialBeat || 0) * beatDur;
    const seekTime = (seekPercent / 100) * duration;
    const musical = seekTime - offset;
    const snapped = Math.round(musical / barDur) * barDur + offset - 0.05;
    return (Math.max(0, Math.min(snapped, duration)) / duration) * 100;
  } catch (_) {
    return seekPercent;
  }
}

// Monotonic counter for unique per-instance clipPath IDs
let clipIdCounter = 0;

// ─── Static Image Waveform ─────────────────────────────────────────
// Cheap <image href> rendering — used when the gemscape is not active.
const StaticWaveform = ({ href, width, onClick }) => {
  const theme = useTheme()

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      width={`${width}px`}
      height={theme.gemscape.staticHeight}
      preserveAspectRatio="none"
      className="waveform waveform-static"
      onClick={onClick}
    >
      <image href={href} crossOrigin="anonymous" width="100%" height={theme.gemscape.staticHeight} />
    </svg>
  );
};

// ─── Inline Active Waveform ────────────────────────────────────────
// Full inline SVG with individual shape DOM nodes — used when hovered/playing.
// `revealMode` adds a class so CSS can differentiate playing-progress vs hover-reveal.
const ActiveWaveform = ({ markup, viewBox, width, onClick, revealMode, isMainPlayer }) => {
  const theme = useTheme()
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      width={`${width}px`}
      height={theme.gemscape.staticHeight}
      viewBox={viewBox || `0 0 100 ${parseInt(theme.gemscape.staticHeight)}`}
      preserveAspectRatio="none"
      overflow="visible"
      className={`waveform gemscape-active${revealMode ? ` ${revealMode}` : ""}${isMainPlayer ? " gemscape-main" : ""}`}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  )
};

// ─── Main Waveform Component ────────────────────────────────────────
const Waveform = ({
  visualizationSVG,
  index,
  isMainPlayer,
  playlist,
  id,
  hovered,
  noFormat,
  isVariation,
  onSelect,
  track
}) => {
  const [waveformWidth, setWaveformWidth] = useState(0);
  const [lastPlayedTrack, setLastPlayedTrack] = useState();
  const queue = useContext(QueueContext);
  const svgContainer = useRef(null);
  const svgWidth = useRef(null);
  const theme = useTheme()

  // Unique clipPath ID for this Waveform instance (stable across re-renders)
  const clipId = useRef(`base-clip-${++clipIdCounter}`);

  // Cached array of .layer-top shape elements — populated by useLayoutEffect
  // when the active SVG mounts, avoids repeated querySelectorAll on hot paths.
  const topShapesRef = useRef([]);

  // After the first paint the intro animation has played — suppress it on
  // all subsequent element swaps (static ↔ active tier transitions).
  const hasAnimated = useRef(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => { hasAnimated.current = true; });
    return () => cancelAnimationFrame(id);
  }, []);

  const isCurrentTrack =
    queue.currentTrack && queue.currentTrack.track
      ? id === queue.currentTrack.track.id
      : false;

  // Main player is always showing the current track, so derive playing
  // directly from queue state rather than requiring an id prop match.
  const isCurrentlyPlaying =
    isMainPlayer === "main-player"
      ? queue.playing
      : isCurrentTrack && queue.playing;

  // Resolve the SVG URL — track waveform or main player's current track
  const svgUrl = visualizationSVG
    || (queue.currentTrack?.track?.waveform)
    || lastPlayedTrack?.waveform;

  // ── Gemscape hook: manages static → primed → active lifecycle ─────
  // Keep gemscape active while the track is loaded in the player (even
  // when paused) so shapes, colors, and progress state persist.
  const isCurrentInPlayer = isCurrentTrack || isMainPlayer === "main-player";
  const { tier, markup, viewBox } = useGemscape({
    url: svgUrl,
    hovered: !!hovered,
    isPlaying: isCurrentlyPlaying || isCurrentInPlayer,
    containerRef: svgContainer,
  });

  const isActive = tier === "active";

  useEffect(() => {
    if (queue.currentTrack && queue.currentTrack.track) {
      setLastPlayedTrack({ ...queue.currentTrack.track });
    }
  }, [queue.currentTrack]);

  // ── Width tracking (no render-phase setState) ────────────────────
  useLayoutEffect(() => {
    const element = svgContainer.current;
    if (!element) return;

    const syncWidth = () => {
      const nextWidth = element.clientWidth;
      if (nextWidth !== svgWidth.current) {
        svgWidth.current = nextWidth;
        setWaveformWidth(nextWidth);
      }
    };

    syncWidth();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(syncWidth);
      resizeObserver.observe(element);
    }

    window.addEventListener("resize", syncWidth);
    return () => {
      window.removeEventListener("resize", syncWidth);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // ── Playback progress / hover reveal on base shapes ────────────────
  const isPlayingProgress = isActive && (isCurrentTrack || isMainPlayer === "main-player");
  const isHoverReveal = isActive && !isPlayingProgress;

  // Closure-safe ref for percentagePlayed — read by usePulseLoop
  const percentagePlayedRef = queue.percentagePlayedRef;

  // Determine which CSS mode class to put on the SVG
  const revealMode = isPlayingProgress
    ? "reveal-playing"
    : isHoverReveal
      ? "reveal-hover"
      : null;

  // ── Hook: shape activation (hover + playback class management) ────
  const {
    handleShapeActivation,
    handleShapeDeactivation,
    hoverFraction,
    promoteShapes,
    updateShapeClasses,
    resetShapeState,
    shapeState,
  } = useShapeActivation({
    topShapesRef,
    svgContainer,
    isPlayingProgress,
    isActive,
    markup,
  });

  // ── Hook: playback progress (base rect + shape-played toggling) ────
  const { updateHoverRect, resetHoverRect } = usePlaybackProgress({
    svgContainer,
    topShapesRef,
    isPlayingProgress,
    isActive,
    percentagePlayed: queue.percentagePlayedRef,
    updateShapeClasses,
    markup,
  });

  // ── Hook: audio-reactive pulse loop ────────────────────────────────
  usePulseLoop({
    shapeState,
    svgContainer,
    isCurrentlyPlaying,
    isActive,
    percentagePlayedRef,
    promoteShapes,
  });

  // ── Seek handler ──────────────────────────────────────────────────
  const seek = (evt) => {
    evt.preventDefault();
    const dim = evt.currentTarget.getBoundingClientRect();
    const x = evt.clientX - dim.left;
    let seekTo = (x / dim.width) * 100;
    // Quantize to nearest bar-line when tempo metadata is available
    const trackObj = track || (playlist && playlist[index]);
    seekTo = snapToBar(seekTo, trackObj);
    // Clear stale shapes immediately so a backward seek
    // doesn't leave old shapes lit until the next pulse.
    resetShapeState();
    queue.setPercentagePlayedRef(seekTo);
    queue.setSeeked(true);
  };

  // ── Click handler (shared by background & foreground) ─────────────
  const handleClick = (event) => {
    if (isMainPlayer === "main-player") {
      queue.setSeeked(true);
      seek(event);
      queue.setPlaying(true);
      return;
    }

    queue.setPlaying(true);
    if (isCurrentTrack) {
      if (!isVariation) {
        queue.setPlaylist(playlist, noFormat);
      } else {
        queue.playVariation(track);
      }
      queue.setSeeked(true);
      seek(event);
      if (onSelect) onSelect(track);
    } else {
      if (!isVariation) {
        queue.setPlaylist(playlist, noFormat);
        queue.setCurrent(index);
      } else {
        queue.playVariation(track);
      }
      queue.setSeeked(true);
      seek(event);
      if (onSelect) onSelect(track);
    }
  };

  // ── Compute live fractions once the active overlay mounts ─────────
  useEffect(() => {
    if (!isActive || !markup) return;
    const id = requestAnimationFrame(() => {
      const svg = svgContainer.current?.querySelector('svg.gemscape-active');
      if (svg) delete svg.dataset.fractionsComputed;
      computeLiveFractions(svgContainer.current);
    });
    return () => cancelAnimationFrame(id);
  }, [isActive, markup]);

  // ── Cache .layer-top shape elements for hot-path access ───────────
  useLayoutEffect(() => {
    if (isActive && markup) {
      const container = svgContainer.current;
      topShapesRef.current = container
        ? Array.from(container.querySelectorAll('.layer-top'))
        : [];
    } else {
      topShapesRef.current = [];
    }
  }, [isActive, markup]);

  // ── Main player entrance: JS-driven pop-in via CSS transitions ────
  useLayoutEffect(() => {
    if (!isActive || !markup || isMainPlayer !== "main-player") return;
    const container = svgContainer.current;
    if (!container) return;

    // Bases: start at scaleY(0.5)
    const bases = container.querySelectorAll(
      '.gemscape-active .base-unplayed, .gemscape-active .base-played'
    );
    bases.forEach(b => {
      b.style.transition = 'none';
      b.style.transform = 'scaleY(0.5)';
    });

    // Gems: start at scale(0) + hidden
    const shapes = container.querySelectorAll('.gemscape-active .layer-top');
    shapes.forEach(s => {
      s.style.transition = 'none';
      s.style.transform = 'scale(0)';
      s.style.opacity = '0';
    });

    // Next frame: clear inline overrides → CSS transitions kick in
    const raf = requestAnimationFrame(() => {
      bases.forEach(b => {
        b.style.transition = `transform 1s ${theme.gemscape.easingStyle.elasticPop}`;
        b.style.transform = '';
      });
      shapes.forEach((s) => {
        const delay = Math.random() * 600;
        s.style.transition = `transform 0.7s ${theme.gemscape.easingStyle.elasticPop} ${delay.toFixed(0)}ms, opacity 0.7s ${theme.gemscape.easingStyle.elasticPop} ${delay.toFixed(0)}ms`;
        s.style.transform = '';
        s.style.opacity = '';
      });
      // After transitions settle, remove inline transition so the normal
      // .gemscape-shape transition rules take over for hover/pulse/etc.
      setTimeout(() => {
        bases.forEach(b => { b.style.transition = ''; });
        shapes.forEach(s => { s.style.transition = ''; });
        const svg = container.querySelector('svg.gemscape-active');
        if (svg) delete svg.dataset.fractionsComputed;
        computeLiveFractions(container);
      }, 1400); // 700ms animation + 600ms max delay + margin
    });

    return () => cancelAnimationFrame(raf);
  }, [isActive, markup, isMainPlayer, theme.gemscape.easingStyle.elasticPop]);

  const [displayPercentage, setDisplayPercentage] = useState(
    queue.percentagePlayedRef.current
  );

  useLayoutEffect(() => {
    //{/* Progress overlay — lightweight tint for non-active tier */}
    if (isActive || (!isMainPlayer && !isCurrentTrack)) return;
    let frameId;
    const tick = () => {
      setDisplayPercentage(queue.percentagePlayedRef.current);
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [isActive, isMainPlayer, isCurrentTrack, queue.percentagePlayedRef])
  // ── Render ────────────────────────────────────────────────────────
  return (
    <Container
      isMainPlayer={isMainPlayer}
      ref={svgContainer}
      hovered={hovered}
      isCurrentTrack={isCurrentTrack}
      className={isCurrentTrack ? "is-current-track" : ""}
      suppressAnimation={hasAnimated.current}
      onMouseMove={(e) => {
        handleShapeActivation(e);
        updateHoverRect(hoverFraction.current);
      }}
      onMouseLeave={() => {
        handleShapeDeactivation();
        resetHoverRect();
      }}
      onPointerLeave={() => {
        handleShapeDeactivation();
        resetHoverRect();
      }}
    >
      {/* Static base — always rendered, no DOM-swap flash */}
      <StaticWaveform
        href={svgUrl}
        width={waveformWidth}
        onClick={handleClick}
      />
      {/* Active overlay — rendered on top when hovered or playing */}
      {isActive && markup && (
        <ActiveWaveform
          markup={markup.replace(/__BASE_CLIP_ID__/g, clipId.current)}
          viewBox={viewBox}
          width={waveformWidth}
          onClick={handleClick}
          revealMode={revealMode}
          isMainPlayer={isMainPlayer === "main-player"}
        />
      )}
      {/* Progress overlay — lightweight tint for non-active tier */}
      {!isActive && (isMainPlayer || isCurrentTrack) && (
        <div
          className="progress-overlay"
          style={{ width: `${displayPercentage}%` }}
        />
      )}
    </Container>
  );
};

export default Waveform;
